import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react';

interface AudioContextType {
  activeRecorderId: string | null;
  registerRecording: (id: string) => void;
  unregisterRecording: (id: string) => void;
  activePlayerId: string | null;
  registerPlayback: (id: string) => void;
  unregisterPlayback: (id: string) => void;
}

const AudioContext = createContext<AudioContextType | undefined>(undefined);

export const AudioProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [activeRecorderId, setActiveRecorderId] = useState<string | null>(null);
  const [activePlayerId, setActivePlayerId] = useState<string | null>(null);

  const registerRecording = useCallback((id: string) => {
    setActiveRecorderId(id);
    // When starting recording, stop any active playback
    setActivePlayerId(null);
  }, []);

  const unregisterRecording = useCallback((id: string) => {
    setActiveRecorderId((prev) => (prev === id ? null : prev));
  }, []);

  const registerPlayback = useCallback((id: string) => {
    setActivePlayerId(id);
    // When starting playback, stop any active recording
    setActiveRecorderId(null);
  }, []);

  const unregisterPlayback = useCallback((id: string) => {
    setActivePlayerId((prev) => (prev === id ? null : prev));
  }, []);

  return (
    <AudioContext.Provider
      value={{
        activeRecorderId,
        registerRecording,
        unregisterRecording,
        activePlayerId,
        registerPlayback,
        unregisterPlayback,
      }}
    >
      {children}
    </AudioContext.Provider>
  );
};

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (context === undefined) {
    return {
      activeRecorderId: null,
      registerRecording: () => {},
      unregisterRecording: () => {},
      activePlayerId: null,
      registerPlayback: () => {},
      unregisterPlayback: () => {},
    };
  }
  return context;
};
